/**
 * ポータル: 各種申請（有給・慶弔・住所変更などの汎用ワークフロー）
 * タブ: 申請する／自分の申請／承認（管理者）／様式マスタ（管理者）
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FileCheck, ChevronRight, Plus, Trash2 } from 'lucide-react';
import * as api from '../../services/api';
import { Modal, StatusBadge, Tabs, fieldStyle, labelStyle, fmtDateTime, EmptyState } from './ui';

interface FieldDef { key: string; label: string; type: string; required?: boolean; options?: string[] }
interface Form { id: string; name: string; description: string | null; fields: FieldDef[]; is_active: number }
interface RequestRow {
  id: string; form_id: string; form_name: string | null;
  applicant_user_id: string; applicant_name: string | null;
  data: Record<string, unknown>; status: string; status_label: string;
  approver_name: string | null; acted_at: string | null; comment: string | null; created_at: string;
  fields?: FieldDef[];
}

export default function PortalRequestsPage() {
  const user = useMemo(() => { try { return JSON.parse(localStorage.getItem('admin_user') ?? '{}'); } catch { return {}; } }, []);
  const isAdmin = ['system_admin', 'shore_admin'].includes(user?.user_role ?? '');

  const [tab, setTab] = useState('apply');
  const [forms, setForms] = useState<Form[]>([]);
  const [mine, setMine] = useState<RequestRow[]>([]);
  const [all, setAll] = useState<RequestRow[]>([]);
  const [applyForm, setApplyForm] = useState<Form | null>(null);
  const [detail, setDetail] = useState<RequestRow | null>(null);
  const [formEditor, setFormEditor] = useState<{ form: Form | null } | null>(null);

  const loadForms = useCallback(() => {
    api.get<{ data: Form[] }>('/portal/requests/forms', isAdmin ? { all: '1' } : {}).then(r => setForms(r.data ?? [])).catch(() => { /* noop */ });
  }, [isAdmin]);
  const loadMine = useCallback(() => {
    api.get<{ data: RequestRow[] }>('/portal/requests', { mine: '1' }).then(r => setMine(r.data ?? [])).catch(() => { /* noop */ });
  }, []);
  const loadAll = useCallback(() => {
    if (isAdmin) api.get<{ data: RequestRow[] }>('/portal/requests').then(r => setAll(r.data ?? [])).catch(() => { /* noop */ });
  }, [isAdmin]);

  useEffect(() => { loadForms(); loadMine(); loadAll(); }, [loadForms, loadMine, loadAll]);

  const openDetail = async (id: string) => {
    try { const r = await api.get<{ data: RequestRow }>(`/portal/requests/${id}`); setDetail(r.data); }
    catch (e: unknown) { alert(e instanceof Error ? e.message : '取得に失敗しました'); }
  };

  const doAction = async (r: RequestRow, action: string, comment?: string) => {
    try {
      await api.post(`/portal/requests/${r.id}/action`, { action, comment: comment ?? null });
      setDetail(null); loadMine(); loadAll();
    } catch (e: unknown) { alert(e instanceof Error ? e.message : '操作に失敗しました'); }
  };

  const requestTable = (rows: RequestRow[], showApplicant: boolean) => (
    <div className="card" style={{ padding: 0, overflow: 'auto' }}>
      {rows.length === 0 ? <EmptyState img="/portal/empty-requests.png" text="申請はまだありません" /> : (
        <table>
          <thead><tr>
            <th>様式</th>{showApplicant && <th style={{ width: 120 }}>申請者</th>}
            <th style={{ width: 150 }}>申請日時</th><th style={{ width: 110 }}>状態</th>
            <th style={{ width: 150 }}>処理</th>
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => void openDetail(r.id)}>
                <td style={{ fontWeight: 600 }}>{r.form_name}</td>
                {showApplicant && <td style={{ fontSize: 12.5 }}>{r.applicant_name}</td>}
                <td style={{ fontSize: 12.5 }}>{fmtDateTime(r.created_at)}</td>
                <td><StatusBadge status={r.status} label={r.status_label} /></td>
                <td style={{ fontSize: 12 }}>{r.approver_name ? `${r.approver_name}（${fmtDateTime(r.acted_at)}）` : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <div className="page-wrap">
      <div className="top-bar">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FileCheck size={20} /> 各種申請
        </h1>
      </div>

      <Tabs active={tab} onChange={setTab} tabs={[
        { key: 'apply', label: '申請する' },
        { key: 'mine', label: '自分の申請' },
        ...(isAdmin ? [
          { key: 'approve', label: `承認（${all.filter(r => r.status === 'pending').length}）` },
          { key: 'forms', label: '様式マスタ' },
        ] : []),
      ]} />

      {tab === 'apply' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {forms.filter(f => f.is_active === 1).map(f => (
            <button key={f.id} className="card" onClick={() => setApplyForm(f)}
              style={{ textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, padding: '18px 18px', border: '1px solid #e5e7eb' }}>
              <div style={{ width: 42, height: 42, borderRadius: 11, background: '#eef6f3', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <FileCheck size={20} color="#0f766e" />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14.5, color: '#111827' }}>{f.name}</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{f.description}</div>
              </div>
              <ChevronRight size={16} color="#cbd5e1" style={{ marginLeft: 'auto', flexShrink: 0 }} />
            </button>
          ))}
        </div>
      )}

      {tab === 'mine' && requestTable(mine, false)}
      {tab === 'approve' && isAdmin && requestTable(all, true)}

      {tab === 'forms' && isAdmin && (
        <div className="card" style={{ padding: 16, maxWidth: 680 }}>
          <button className="btn-p" style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }} onClick={() => setFormEditor({ form: null })}>
            <Plus size={14} /> 様式を追加
          </button>
          {forms.map(f => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 0', borderBottom: '1px solid #f8fafc' }}>
              <span style={{ fontSize: 13.5, fontWeight: 600, flex: 1, color: f.is_active ? '#111827' : '#6b7280' }}>
                {f.name}{f.is_active === 0 && '（無効）'}
                <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: 8, fontSize: 12 }}>{f.fields.length}項目</span>
              </span>
              <button className="btn" style={{ fontSize: 12 }} onClick={() => setFormEditor({ form: f })}>編集</button>
              <button className="btn" style={{ fontSize: 12, color: '#dc2626' }} onClick={async () => {
                if (!confirm(`様式「${f.name}」を削除しますか？`)) return;
                try { await api.del(`/portal/requests/forms/${f.id}`); loadForms(); }
                catch (e: unknown) { alert(e instanceof Error ? e.message : '失敗しました'); }
              }}>削除</button>
            </div>
          ))}
        </div>
      )}

      {applyForm && (
        <ApplyModal form={applyForm} onClose={() => setApplyForm(null)}
          onSaved={() => { setApplyForm(null); loadMine(); setTab('mine'); }} />
      )}

      {detail && (
        <Modal width={600} onClose={() => setDetail(null)} title={
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {detail.form_name} <StatusBadge status={detail.status} label={detail.status_label} />
          </span>
        }>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
            申請者: {detail.applicant_name} ・ {fmtDateTime(detail.created_at)}
          </div>
          {(detail.fields ?? []).map(f => (
            <div key={f.key} style={{ display: 'flex', fontSize: 13.5, padding: '6px 0', borderBottom: '1px solid #f8fafc' }}>
              <span style={{ width: 150, color: '#6b7280', flexShrink: 0 }}>{f.label}</span>
              <span style={{ whiteSpace: 'pre-wrap' }}>{String(detail.data[f.key] ?? '—')}</span>
            </div>
          ))}
          {detail.comment && (
            <div style={{ fontSize: 13, color: '#4b5563', marginTop: 10 }}>処理コメント: {detail.comment}（{detail.approver_name}）</div>
          )}
          <DetailActions r={detail} isAdmin={isAdmin} meId={user?.id ?? ''} onAction={doAction} />
        </Modal>
      )}

      {formEditor && (
        <FormEditor form={formEditor.form} onClose={() => setFormEditor(null)}
          onSaved={() => { setFormEditor(null); loadForms(); }} />
      )}
    </div>
  );
}

function DetailActions({ r, isAdmin, meId, onAction }: {
  r: RequestRow; isAdmin: boolean; meId: string;
  onAction: (r: RequestRow, action: string, comment?: string) => void;
}) {
  const [comment, setComment] = useState('');
  if (r.status !== 'pending') return null;
  return (
    <div style={{ marginTop: 16, background: '#f8fafc', borderRadius: 10, padding: 14 }}>
      {isAdmin ? (
        <>
          <input style={{ ...fieldStyle, marginBottom: 10 }} value={comment} onChange={e => setComment(e.target.value)} placeholder="コメント（任意）" />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-p" onClick={() => onAction(r, 'approve', comment)}>承認する</button>
            <button className="btn" style={{ color: '#dc2626' }} onClick={() => onAction(r, 'reject', comment)}>却下する</button>
            {r.applicant_user_id === meId && (
              <button className="btn" onClick={() => { if (confirm('取り下げますか？')) onAction(r, 'withdraw'); }}>取下げ</button>
            )}
          </div>
        </>
      ) : r.applicant_user_id === meId ? (
        <button className="btn" style={{ color: '#dc2626' }} onClick={() => { if (confirm('この申請を取り下げますか？')) onAction(r, 'withdraw'); }}>取下げる</button>
      ) : null}
    </div>
  );
}

function ApplyModal({ form, onClose, onSaved }: { form: Form; onClose: () => void; onSaved: () => void }) {
  const [data, setData] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const set = (k: string, v: string) => setData(d => ({ ...d, [k]: v }));

  const submit = async () => {
    for (const f of form.fields) {
      if (f.required && !(data[f.key] ?? '').trim()) { alert(`「${f.label}」を入力してください`); return; }
    }
    setBusy(true);
    try { await api.post('/portal/requests', { form_id: form.id, data }); onSaved(); }
    catch (e: unknown) { alert(e instanceof Error ? e.message : '申請に失敗しました'); }
    finally { setBusy(false); }
  };

  return (
    <Modal width={540} title={form.name} onClose={onClose}>
      {form.description && <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 14px' }}>{form.description}</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {form.fields.map(f => (
          <div key={f.key}>
            <label style={labelStyle}>{f.label}{f.required && <span style={{ color: '#dc2626' }}> *</span>}</label>
            {f.type === 'textarea' ? (
              <textarea style={{ ...fieldStyle, minHeight: 80, resize: 'vertical' }} value={data[f.key] ?? ''} onChange={e => set(f.key, e.target.value)} />
            ) : f.type === 'select' ? (
              <select style={fieldStyle} value={data[f.key] ?? ''} onChange={e => set(f.key, e.target.value)}>
                <option value="">選択してください</option>
                {(f.options ?? []).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <input type={f.type === 'date' ? 'date' : f.type === 'number' ? 'number' : 'text'}
                style={fieldStyle} value={data[f.key] ?? ''} onChange={e => set(f.key, e.target.value)} />
            )}
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn" onClick={onClose}>キャンセル</button>
          <button className="btn-p" disabled={busy} onClick={() => void submit()}>{busy ? '送信中...' : '申請する'}</button>
        </div>
      </div>
    </Modal>
  );
}

function FormEditor({ form, onClose, onSaved }: { form: Form | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(form?.name ?? '');
  const [description, setDescription] = useState(form?.description ?? '');
  const [fields, setFields] = useState<FieldDef[]>(form?.fields ?? [{ key: 'field1', label: '', type: 'text', required: true }]);
  const [isActive, setIsActive] = useState(form ? form.is_active === 1 : true);
  const [busy, setBusy] = useState(false);

  const setField = (i: number, patch: Partial<FieldDef>) =>
    setFields(fs => fs.map((f, j) => j === i ? { ...f, ...patch } : f));

  const save = async () => {
    if (!name.trim()) { alert('様式名を入力してください'); return; }
    const cleaned = fields.filter(f => f.label.trim()).map((f, i) => ({
      key: f.key || `field${i + 1}`, label: f.label.trim(), type: f.type,
      required: !!f.required,
      ...(f.type === 'select' ? { options: (f.options ?? []).filter(Boolean) } : {}),
    }));
    if (cleaned.length === 0) { alert('入力項目を1つ以上作ってください'); return; }
    setBusy(true);
    try {
      const payload = { name: name.trim(), description: description || null, fields: cleaned, is_active: isActive };
      if (form) await api.put(`/portal/requests/forms/${form.id}`, payload);
      else await api.post('/portal/requests/forms', payload);
      onSaved();
    } catch (e: unknown) { alert(e instanceof Error ? e.message : '保存に失敗しました'); }
    finally { setBusy(false); }
  };

  return (
    <Modal width={720} title={form ? '様式の編集' : '様式を追加'} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>様式名 *</label>
            <input style={fieldStyle} value={name} onChange={e => setName(e.target.value)} placeholder="例: 残業申請" />
          </div>
          <div style={{ flex: 2 }}>
            <label style={labelStyle}>説明</label>
            <input style={fieldStyle} value={description ?? ''} onChange={e => setDescription(e.target.value)} />
          </div>
        </div>
        <div>
          <label style={labelStyle}>入力項目</label>
          {fields.map((f, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
              <input style={{ ...fieldStyle, flex: 2 }} value={f.label} placeholder="項目名（例: 開始日）"
                onChange={e => setField(i, { label: e.target.value })} />
              <select style={{ ...fieldStyle, width: 110 }} value={f.type} onChange={e => setField(i, { type: e.target.value })}>
                <option value="text">1行テキスト</option>
                <option value="textarea">複数行</option>
                <option value="date">日付</option>
                <option value="number">数値</option>
                <option value="select">選択式</option>
              </select>
              {f.type === 'select' && (
                <input style={{ ...fieldStyle, flex: 2 }} value={(f.options ?? []).join(',')} placeholder="選択肢をカンマ区切りで"
                  onChange={e => setField(i, { options: e.target.value.split(/[、,]/).map(s => s.trim()) })} />
              )}
              <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' }}>
                <input type="checkbox" checked={!!f.required} onChange={e => setField(i, { required: e.target.checked })} /> 必須
              </label>
              <button className="btn" style={{ padding: '6px 8px' }} onClick={() => setFields(fs => fs.filter((_, j) => j !== i))}>
                <Trash2 size={13} color="#dc2626" />
              </button>
            </div>
          ))}
          <button className="btn" style={{ fontSize: 12.5 }} onClick={() => setFields(fs => [...fs, { key: `field${fs.length + 1}`, label: '', type: 'text' }])}>
            ＋ 項目を追加
          </button>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} /> 有効（申請できる状態にする）
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn" onClick={onClose}>キャンセル</button>
          <button className="btn-p" disabled={busy} onClick={() => void save()}>{busy ? '保存中...' : '保存する'}</button>
        </div>
      </div>
    </Modal>
  );
}
